
import { ciIncludes, groupBy, unaccent } from "@/lib/util";
import { Component, MixedVue } from "@/vue";
import { Prop, Watch } from "vue-property-decorator";
import { isMobile } from "../lib/util";

interface MultiSelectItem<T> {
  label: any;
  unique: any;
  groupLabel: any;
  shortLabel: any;
  description: any;
  option: T;
  key: string;
  selected: boolean;
}

@Component({
  name: "MultiSelect",
})
export default class MultiSelect<T> extends MixedVue {
  $props: {
    options: T[];
    placeholder?: string;
    hideCheckbox?: boolean;
    multiple?: boolean;
    disabled?: boolean;
    searchable?: boolean;
    clearable?: boolean;
    groupLabel?: keyof T;
    defaultGroupName?: string;
    label?: keyof T | ((item?: T) => string);
    labelDescription?: keyof T | ((item?: T) => string);
    shortLabel?: keyof T | ((item?: T) => string);
    unique?: keyof T | ((item?: T) => string);
    value?: any;
    multipleSelectedLabel?: any;
    isSelected?: (item: T) => boolean;
    dropDownDirection?: "left" | "right";
    theme?: "" | "action" | "minimal" | "field" | "hidden";
    showGroupNames?: boolean;
    showSelectedGroup?: boolean;
    mobileMode?: "normal" | "fullscreen";
  };

  @Prop({ type: Array, required: true })
  options: T[];

  @Prop({ type: String, required: false, default: null })
  placeholder: string;

  @Prop({ type: Boolean, required: false, default: false })
  hideCheckbox: boolean;

  /**
   * Allow multiple selection.
   *
   * WARNING! multiple selection doesn't have great
   * native support, so the custom one will be in
   * use.
   */
  @Prop({ type: Boolean, required: false, default: false })
  multiple: boolean;

  @Prop({ type: Boolean, required: false, default: false })
  disabled: boolean;

  @Prop({ type: Boolean, required: false, default: false })
  searchable: boolean;

  @Prop({ type: Boolean, required: false, default: true })
  clearable: boolean;

  @Prop({ type: String, required: false, default: null })
  groupLabel: keyof T;

  @Prop({ type: String, required: false, default: "" })
  defaultGroupName: string;

  @Prop({ type: [String, Function], required: false, default: null })
  label: keyof T | ((item?: T) => string);

  @Prop({ type: [String, Function], required: false, default: null })
  labelDescription: keyof T | ((item?: T) => string);

  @Prop({ type: [String, Function], required: false, default: null })
  shortLabel: keyof T | ((item?: T) => string);

  @Prop({ type: [String, Function], required: false, default: null })
  unique: keyof T | ((item?: T) => string);

  @Prop({ type: [String, Number, Object], required: false, default: null })
  value: any;

  @Prop({ type: [String, Function], required: false, default: null })
  multipleSelectedLabel: any;

  @Prop({ type: Function, required: false, default: null })
  isSelected: (item: T) => boolean;

  @Prop({ type: String, required: false, default: "left" })
  dropDownDirection: "left" | "right";

  @Prop({ type: String, required: false, default: "" })
  theme: "" | "action" | "minimal" | "field" | "hidden";

  @Prop({ type: Boolean, required: false, default: true })
  showGroupNames: boolean;

  @Prop({ type: Boolean, required: false, default: false })
  showSelectedGroup: boolean;

  @Prop({ type: String, required: false, default: "fullscreen" })
  mobileMode: "normal" | "fullscreen";

  items: MultiSelectItem<T>[] = [];
  selectedGroup = "";
  selectedLabel = "";
  searchString = "";
  showDropDown = false;
  shouldFlip = false;
  areItemsReversed = false;
  nativeSelectModel = "";

  get fullscreen() {
    return isMobile() && this.mobileMode == "fullscreen";
  }

  get computedPlaceholder() {
    return this.placeholder || this.$gettext("Select item");
  }

  get filteredItems() {
    const str = unaccent(this.searchString);
    return this.items.filter((item) => ciIncludes(unaccent(item.label.toString()), str));
  }

  get selectedItems() {
    return this.items.filter((item) => this.isItemSelected(item));
  }

  get selectedItemsCount() {
    return this.selectedItems.length;
  }

  get selectedOptions() {
    return this.selectedItems.map((item) => item.option);
  }

  get groupedFilteredItems(): Record<string, MultiSelectItem<T>[]> {
    return groupBy(this.filteredItems, (item) => item.groupLabel);
  }

  get selectedGroupVisible() {
    return this.selectedGroup && this.showSelectedGroup;
  }

  @Watch("value")
  onValueChange(toValue: T) {
    if (this.multiple) return;

    this.items.forEach((item) => {
      item.selected = this.itemValueEqual(item, toValue);
      if (item.selected) {
        this.nativeSelectModel = item.key;
      }
    });
    this.computeSelectedLabel();
  }

  shouldFlipDropdown() {
    const dropdownRect = (this.$refs.dropDownRef as HTMLElement).getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const dropdownDistanceThreshold = 15; //px

    this.shouldFlip = dropdownRect.bottom + dropdownDistanceThreshold > windowHeight;

    if (this.shouldFlip !== this.areItemsReversed) {
      this.items.reverse();
      this.areItemsReversed = this.shouldFlip;
    }
    return this.items;
  }

  @Watch("$language.current")
  onLanguageChange() {
    this.computeSelectedLabel();
  }

  @Watch("selectedItemsCount")
  onSelectedItemsCountChange() {
    this.computeSelectedLabel();
  }

  @Watch("options")
  onOptionsChange() {
    this.initOptions();
  }

  mounted() {
    this.initOptions();
  }

  deselectAll() {
    if (!this.clearable || !this.multiple) return;
    this.items
      .filter((item) => item.selected)
      .forEach((item) => {
        item.selected = false;
        this.$emit("change", item.option, item.selected);
      });
    this.$emit("input", this.selectedOptions);
    this.showDropDown = false;
  }

  maybeShowDropDown() {
    if (this.showDropDown) return;

    this.showDropDown = true;
    this.$emit("show");
    this.$nextTick(() => {
      this.$el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      this.shouldFlipDropdown();
    });
  }

  maybeHideDropDown(focusEvent: FocusEvent) {
    const dropdown = this.$el.querySelector(".drop-down");
    if (!dropdown || !focusEvent || !focusEvent.relatedTarget || !dropdown.contains(focusEvent.relatedTarget as HTMLElement)) {
      this.searchString = "";
      this.showDropDown = false;
      this.shouldFlip = false;
    }
  }

  itemValueEqual(item: MultiSelectItem<T>, value: T) {
    return item.unique === value || item.shortLabel === value || item.label === value || item.option === value;
  }

  initOptions() {
    this.items = this.options.map((option, index) => {
      const label = this.getOptionProperty(option, this.label) || option;
      const unique = this.getOptionProperty(option, this.unique) || label;

      const item = {
        label,
        unique,
        groupLabel: this.getOptionProperty(option, this.groupLabel) || this.defaultGroupName,
        shortLabel: this.getOptionProperty(option, this.shortLabel) || label,
        description: this.getOptionProperty(option, this.labelDescription),
        option,
        key: `dropdown-${unique}-${index}`,
        selected: false,
      };
      item.selected = this.isItemSelected(item);
      if (item.selected) {
        this.nativeSelectModel = item.key;
      }
      return item;
    });
    this.computeSelectedLabel();
  }

  getOptionProperty(option: T, prop: MultiSelect<T>["label"]) {
    if (!prop) {
      return;
    }

    if (typeof prop === "function") {
      return prop(option);
    }

    return option[prop];
  }

  handleNativeSelect() {
    if (this.multiple) return;

    const opt = (this.$refs.nativeSelect as HTMLSelectElement).selectedOptions[0];
    const nativeSelection = opt && opt.value;

    if (!nativeSelection) {
      // Clear selection
      this.itemClick(this.selectedItems[0]);
    } else {
      this.itemClick(this.items.find((item) => item.key === nativeSelection));
    }
  }

  itemClick(item: MultiSelectItem<T>) {
    if (this.multiple) {
      item.selected = !item.selected;
      this.$emit("input", this.selectedOptions);
      this.$emit("change", item.option, item.selected);
    } else if (item.selected && this.clearable) {
      item.selected = false;
      this.$emit("input", item.option);
      this.showDropDown = false;
    } else {
      this.items.filter((item) => item.selected).forEach((item) => (item.selected = false));
      item.selected = true;
      this.$emit("input", item.option);
      this.showDropDown = false;
    }
    this.computeSelectedLabel();
  }

  isItemSelected(item: MultiSelectItem<T>) {
    return (
      this.value === item.unique ||
      this.value === item.label ||
      this.value === item.shortLabel ||
      this.value === item.option ||
      (this.isSelected && this.isSelected(item.option)) ||
      item.selected
    );
  }

  computeSelectedLabel() {
    if (this.selectedItemsCount === 0) {
      this.selectedLabel = "";
    } else if (this.selectedItemsCount === 1) {
      this.selectedGroup = this.selectedItems[0].groupLabel;
      this.selectedLabel = this.selectedItems[0].shortLabel;
    } else if (typeof this.multipleSelectedLabel === "function") {
      this.selectedLabel = this.multipleSelectedLabel(this.selectedOptions);
    } else if (this.multipleSelectedLabel) {
      this.selectedLabel = this.multipleSelectedLabel;
    } else if (this.multiple) {
      this.selectedLabel = this.$gettextInterpolate(this.$ngettext("%{ n } selected", "%{ n } selected", this.selectedItemsCount), {
        n: this.selectedItemsCount,
      });
    } else {
      this.selectedLabel = this.selectedItems[0].shortLabel;
    }
  }
}
